import { useQuery } from "@apollo/client/react";
import {
  GQLQuery,
  GQLMedicalInstitutionMemberStatus,
} from "@rx-cs-system/schema";
import {
  GET_MEDICAL_INSTITUTION,
  GET_MEDICAL_INSTITUTION_MEMBERS_BY_MEDICAL_INSTITUITON_ID,
} from "../../../graphql/queries";
import { parseContracts } from "../../../graphql/utils";
import { useHandleError } from "../../../hooks";

type Options = {
  /** パスワードリセット対象のメンバー一覧も取得するか (V2 の初回契約向け画面でのみ必要) */
  withMembers?: boolean;
};

/**
 * ウェルカムメール再送画面の表示に必要な契約情報を URL のパラメータから取得する。
 *
 * 医療機関詳細からの画面内遷移に依存せず、URL 直アクセス・リロードでも
 * 同じデータが揃うようにするため、画面側ではなくここで取得する。
 */
export const useResendWelcomeMailContract = (
  medicalInstitutionId: string,
  contractId: string,
  { withMembers = false }: Options = {},
) => {
  const getMedicalInstitutionPayload = useQuery<GQLQuery>(
    GET_MEDICAL_INSTITUTION,
    {
      variables: { medicalInstitutionId },
    },
  );

  useHandleError(getMedicalInstitutionPayload.error);

  const getMedicalInstitutionMembersPayload = useQuery<GQLQuery>(
    GET_MEDICAL_INSTITUTION_MEMBERS_BY_MEDICAL_INSTITUITON_ID,
    {
      variables: { medicalInstitutionId },
      skip: !withMembers,
    },
  );

  useHandleError(getMedicalInstitutionMembersPayload.error);

  const medicalInstitutionDetail =
    getMedicalInstitutionPayload.data?.medicalInstitutionDetail;

  const medicalInstitution = medicalInstitutionDetail
    ? {
        name: medicalInstitutionDetail.name,
        address: `${medicalInstitutionDetail.prefecture} ${medicalInstitutionDetail.city} ${medicalInstitutionDetail.otherAddress}`,
      }
    : undefined;

  const contract = parseContracts(
    getMedicalInstitutionPayload.data?.contracts || [],
  ).find((c) => c.id === contractId);

  const members = (
    getMedicalInstitutionMembersPayload.data
      ?.medicalInstitutionMembersByMedicalInstitutionId || []
  )
    .filter((m) => m.status !== GQLMedicalInstitutionMemberStatus.Disabled)
    .map(({ id, name }) => ({ id, name }));

  return {
    loading:
      getMedicalInstitutionPayload.loading ||
      getMedicalInstitutionMembersPayload.loading,
    medicalInstitution,
    contract,
    members,
  };
};
